import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Keyboard, KeyboardAvoidingView, Platform, Pressable, ScrollView, TextInput, useWindowDimensions, View } from 'react-native';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BotSetupComposer from './BotSetupComposer';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import { isDarkTheme } from '@/constants/Colors';
import { BRAND_GREEN, BRAND_GREEN_DEEP, BRAND_GREEN_TINT } from '@/constants/onboarding';
import { useTheme } from '@/context/ThemeContext';
import { useAgentStore } from '@/store';
import { agentsApi } from '@/lib/api';
import { fetchWithAuth, avatarUriToFile, macosUploadFile } from '@/lib/api/client';
import useStartAgentWorkChat from '@/hooks/useStartAgentWorkChat';
import useStartAgentChat from '@/hooks/useStartAgentChat';
import BotModeChoices from './BotModeChoices';

const FIELDS = ['name', 'description', 'system'];
const LABELS = ['Name', 'Core jobs', 'System instructions', 'Avatar'];
const LIMITS = [100, 400, 4000];
const NAME_IDEAS = [
  'Research Partner', 'Garden Planner', 'Writing Coach', 'Travel Planner',
  'Recipe Creator', 'Study Buddy', 'Coding Partner', 'Language Tutor',
  'Book Scout', 'Project Planner', 'Brainstorm Buddy', 'Home Organizer',
  'History Guide', 'Interview Coach', 'Podcast Producer', 'Photography Coach',
  'Meal Planner', 'Science Explorer', 'Story Builder', 'Music Guide',
];
let previousNameIdeas = [];
function pickNameIdeas() {
  const pool = NAME_IDEAS.filter(name => !previousNameIdeas.includes(name));
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  previousNameIdeas = pool.slice(0, 3);
  return previousNameIdeas;
}


function setupAccent(isDark) {
  return {
    text: isDark ? BRAND_GREEN_TINT : BRAND_GREEN_DEEP,
    wash: isDark ? 'rgba(31, 107, 71, 0.32)' : 'rgba(13, 92, 71, 0.10)',
    avatarBg: isDark ? '#14352C' : '#E8F4EE',
    avatarFg: isDark ? BRAND_GREEN_TINT : BRAND_GREEN_DEEP,
    frameBorder: isDark ? BRAND_GREEN : '#B7DCC9',
    frameBg: isDark ? '#0F2A22' : '#F1F8F4',
  };
}

function SetupTextPreview({ text, color, accent, compact = false }) {
  const lines = compact ? 3 : 5;
  const characters = compact ? 180 : 320;
  const [expanded, setExpanded] = useState(false);
  const long = text.length > characters || text.split('\n').length > lines;
  const preview = text.slice(0, characters).split('\n').slice(0, lines).join('\n').trimEnd();
  return <View style={{ gap: 8 }}>
    <Text selectable color={color} numberOfLines={long && !expanded ? lines : undefined} style={{ lineHeight: compact ? 22 : 26 }}>{long && !expanded ? `${preview}…` : text}</Text>
    {long && <Pressable onPress={() => setExpanded(value => !value)} accessibilityRole="button" accessibilityLabel={expanded ? 'Read less' : 'Read more'} accessibilityState={{ expanded }} style={{ alignSelf: 'flex-start', paddingVertical: 8 }}>
      <Text size="s" weight="medium" style={{ color: accent || BRAND_GREEN }}>{expanded ? 'Read less' : 'Read more'}</Text>
    </Pressable>}
  </View>;
}

export default function BotSetupChat({ initialSession }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const compact = width < 600 || height < 800;
  const native = Platform.OS === 'ios' || Platform.OS === 'android';
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  useEffect(() => {
    if (!native) return undefined;
    const show = Keyboard.addListener('keyboardDidShow', () => setKeyboardVisible(true));
    const hide = Keyboard.addListener('keyboardDidHide', () => setKeyboardVisible(false));
    return () => { show.remove(); hide.remove(); };
  }, [native]);
  const router = useRouter();
  const startWork = useStartAgentWorkChat();
  const startChat = useStartAgentChat();
  const addMyAgent = useAgentStore(s => s.addMyAgent);
  const [session, setSession] = useState(initialSession);
  const [input, setInput] = useState('');
  const [nameIdeas] = useState(() => initialSession.nameIdeas?.length === 3 ? initialSession.nameIdeas : pickNameIdeas());
  const [editedSuggestion, setEditedSuggestion] = useState(null);
  const [selectedName, setSelectedName] = useState(initialSession.draft.name || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [drafting, setDrafting] = useState(false);
  const autoDraftAttempt = useRef('');
  const [avatarDraft, setAvatarDraft] = useState(null);
  const [avatarError, setAvatarError] = useState('');
  const [avatarGenerations, setAvatarGenerations] = useState(initialSession.avatarGenerations || 0);
  const avatarLimitReached = avatarGenerations >= 5;
  const avatarVariationsLeft = Math.max(0, 5 - avatarGenerations);
  const [generatingAvatar, setGeneratingAvatar] = useState(false);
  const [created, setCreated] = useState(null);
  const lock = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const scroll = useRef(null);
  const { step } = session;
  useEffect(() => {
    scroll.current?.scrollTo({ y: 0, animated: false });
    if (native) Keyboard.dismiss();
  }, [step, native]);
  const avatarKey = JSON.stringify([session._id, session.draft.name, session.draft.description]);
  const draft = { ...session.draft, avatarUrl: (avatarDraft?.key === avatarKey ? avatarDraft.url : '') || session.draft.avatarUrl };
  const run = async fn => {
    if (lock.current || !mounted.current) return;
    lock.current = true; setBusy(true); setError('');
    try { await fn(); } catch (err) { if (mounted.current) { if (err.data?.avatarLimitReached) setAvatarGenerations(5); setError(err.message || 'Something went wrong. Your saved setup is safe. Try again.'); } }
    finally { lock.current = false; if (mounted.current) setBusy(false); }
  };
  const save = async (nextDraft, nextStep, source = session) => {
    if (!mounted.current) throw new Error('Bot setup was closed');
    const result = await fetchWithAuth(`/json/bot-onboarding/${source._id}`, { method: 'PUT', body: { draft: nextDraft, step: nextStep, revision: source.revision } });
    if (!mounted.current) throw new Error('Bot setup was closed');
    setSession(result.session);
    setEditedSuggestion(null);
    if (nextStep !== source.step) {
      setInput('');
      if (nextStep === 0) setSelectedName(result.session.draft.name || '');
    }

    return result.session;
  };
  const draftWithArya = async (source, responsibilities = '') => {
    if (!mounted.current) return;
    setDrafting(true);
    try {
      let value;
      try {
        value = await agentsApi.generateContent({ name: source.draft.name, description: responsibilities, generate: 'bot-setup', modelType: 'text' });
      } catch {
        return;
      }
      if (!mounted.current) return;
      if (!value?.description?.trim() || !value?.system?.trim()) return;
      return await save({ ...source.draft, ...value }, source.step, source);
    } finally { setDrafting(false); }
  };
  useEffect(() => {
    if (step !== 1 || draft.description || draft.system || busy) return;
    const key = `${session._id}:${draft.name}`;
    if (autoDraftAttempt.current === key) return;
    autoDraftAttempt.current = key;
    run(() => draftWithArya(session));
  }, [step, draft.name, draft.description, draft.system, busy]);
  const send = (value = input) => run(async () => {
    if (step > 2 || !value.trim()) return;
    const next = { ...draft, [FIELDS[step]]: value.trim() };
    if (step === 0 && next.name !== draft.name) {
      next.description = ''; next.system = ''; next.avatarUrl = '';
    }
    // A failed redraft must not leave the old role's instructions approvable.
    if (step === 1 && next.description !== draft.description) next.system = '';
    const saved = await save(next, step + 1);
    if (step === 1 && (next.description !== draft.description || !draft.system)) await draftWithArya(saved, next.description);
  });
  const approve = () => send(editedSuggestion ?? draft[FIELDS[step]] ?? '');
  const generate = kind => run(async () => {
    if (kind !== 'avatar') return draftWithArya(session, step === 2 ? draft.description : '');
    if (avatarLimitReached) return;
    setGeneratingAvatar(true);
    setAvatarError('');
    try {
      const result = await agentsApi.generateContent({ name: draft.name, description: draft.description, generate: 'avatar', modelType: 'text', onboardingId: session._id });
      const value = typeof result === 'string' ? result : result?.url;
      setAvatarGenerations(count => result?.avatarGenerations ?? count + 1);
      if (typeof value !== 'string' || !value.trim()) throw new Error('Couldn’t create the avatar. Try again or upload your own.');
      // Retain a successful generation even if its subsequent save fails.
      // Approving can retry that save without consuming another variation.
      if (mounted.current) setAvatarDraft({ key: avatarKey, url: value });
      await save({ ...draft, avatarUrl: value }, step);
    } finally { setGeneratingAvatar(false); }
  });
  useEffect(() => {
    if (step < 2 || created || avatarGenerations >= 5 || session.draft.avatarUrl || !session.draft.name || !session.draft.description) return;
    let cancelled = false;
    setGeneratingAvatar(true);
    setAvatarError('');
    // Keep the speculative result local. It joins the next normal save, so
    // finishing an image cannot overwrite newer text or race a revision update.
    agentsApi.generateContent({ name: session.draft.name, description: session.draft.description, generate: 'avatar', modelType: 'text', onboardingId: session._id })
      .then(result => {
        const url = typeof result === 'string' ? result : result?.url;
        if (!cancelled) setAvatarGenerations(count => result?.avatarGenerations ?? count + 1);
        if (typeof url !== 'string' || !url.trim()) throw new Error('Couldn’t create the avatar. Try again or upload your own.');
        if (!cancelled) setAvatarDraft({ key: avatarKey, url });
      })
      .catch(err => { if (!cancelled) { if (err.data?.avatarLimitReached) setAvatarGenerations(5); setAvatarError(err.data?.avatarLimitReached ? 'Keep your current avatar or upload your own.' : 'Couldn’t create the avatar. Try again or upload your own.'); } })
      .finally(() => { if (!cancelled) setGeneratingAvatar(false); });
    return () => { cancelled = true; setGeneratingAvatar(false); };
  }, [avatarKey, step >= 2, created, session.draft.avatarUrl]);
  const upload = () => run(async () => {
    const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, allowsEditing: true, aspect: [1, 1], quality: 0.8 });
    if (result.canceled || !result.assets?.[0]) return;
    const asset = result.assets[0];
    if (asset.fileSize > 5 * 1024 * 1024) throw new Error('Choose a PNG or JPG under 5 MB.');
    const mime = asset.mimeType || 'image/jpeg';
    if (!['image/png', 'image/jpeg'].includes(mime)) throw new Error('Choose a PNG or JPG image.');
    const endpoint = `/json/bot-onboarding/${session._id}/avatar`;
    let data;
    if (Platform.OS === 'macos') {
      data = await macosUploadFile(asset.uri, { endpoint, method: 'POST', fieldName: 'avatar', fileName: 'avatar.jpg', mimeType: mime, extraFields: { revision: String(session.revision) } });
    } else {
      const body = new FormData();
      body.append('revision', String(session.revision));
      body.append('avatar', Platform.OS === 'web' ? await avatarUriToFile(asset.uri, mime, 'avatar.jpg') : { uri: asset.uri, type: mime, name: 'avatar.jpg' });
      data = await fetchWithAuth(endpoint, { method: 'POST', body });
    }
    setAvatarDraft(null);
    setSession(data.session);
  });
  const create = () => run(async () => {
    const ready = step >= 4 ? session : await save({ ...draft }, 4);
    const avatarUrl = draft.avatarUrl || ready.draft.avatarUrl;
    const result = await agentsApi.saveWithAvatar({ fields: { name: ready.draft.name, description: ready.draft.description, system: ready.draft.system, type: 'text', onboardingId: ready._id }, avatar: avatarUrl ? { uri: avatarUrl } : null });
    addMyAgent(result.agent); setCreated(result.agent);
  });
  const brand = setupAccent(isDark);
  const accent = brand.text;
  const titles = ['Who are we creating?', 'Give your bot a purpose', 'Make it work your way', 'Put a face to the name'];
  const subtitles = [
    'Start with a name. Gab AI will take it from there.',
    `Arya’s starting point for ${draft.name}. Keep what fits and make it your own.`,
    'These instructions shape how your bot thinks, works, and responds. Approve the draft or write your own.',
    'Arya creates the first look. Keep it, try another, or upload your own.',
  ];
  const progressStep = created ? 4 : Math.min(step, 3);
  const card = { padding: compact ? 16 : 22, borderRadius: compact ? 18 : 22, borderWidth: 1, borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_secondary };
  const link = (text, onPress, extraStyle, size = 's') => <Pressable disabled={busy} onPress={onPress} accessibilityRole="button" accessibilityLabel={text} style={[{ paddingVertical: 10, paddingHorizontal: 4 }, extraStyle]}><Text size={size} style={{ color: accent }}>{text}</Text></Pressable>;
  const approveButton = (step === 1 || step === 2) && (
    <Pressable onPress={approve} disabled={busy || !(editedSuggestion ?? draft[FIELDS[step]])?.trim()} accessibilityRole="button" accessibilityLabel={step === 1 ? 'Approve responsibilities' : 'Approve instructions'}
      style={({ hovered, pressed }) => ({ alignSelf: 'center', maxWidth: '100%', flexDirection: 'row', gap: 10, alignItems: 'center', justifyContent: 'center', minHeight: 44, paddingHorizontal: 20, paddingVertical: 12, borderRadius: 12, backgroundColor: colors.text_color_primary, opacity: busy || !(editedSuggestion ?? draft[FIELDS[step]])?.trim() ? 0.4 : hovered || pressed ? 0.85 : 1 })}>
      <Text weight="bold" style={{ color: colors.solid_color_primary }}>✓</Text>
      <Text weight="bold" style={{ color: colors.solid_color_primary, flexShrink: 1, textAlign: 'center' }}>{step === 1 ? 'Approve responsibilities' : 'Approve instructions'}</Text>
    </Pressable>
  );
  const avatar = size => draft.avatarUrl
    ? <Image source={{ uri: draft.avatarUrl }} style={{ width: size, height: size, borderRadius: size * 0.3 }} />
    : <View style={{ width: size, height: size, borderRadius: size * 0.3, backgroundColor: brand.avatarBg, alignItems: 'center', justifyContent: 'center' }}><Text style={{ fontSize: size * 0.4, color: brand.avatarFg }}>{draft.name?.slice(0, 1).toUpperCase() || '✦'}</Text></View>;
  return <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1, minHeight: 0, paddingTop: insets.top }}>
    <View style={{ paddingHorizontal: compact ? 16 : 24, paddingVertical: compact ? 4 : 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
      <Pressable onPress={() => router.replace('/agents')} accessibilityRole="button" accessibilityLabel="Back to bots" hitSlop={8} style={{ minHeight: 44, justifyContent: 'center', paddingHorizontal: 4 }}><Text size="s" color="primary">← Back to bots</Text></Pressable>
    </View>
    <ScrollView ref={scroll} style={{ flex: 1, minHeight: 0 }} contentContainerStyle={{ width: '100%', maxWidth: 760, alignSelf: 'center', padding: compact ? 16 : 24, paddingTop: compact ? 8 : 24, paddingBottom: (compact ? 12 : 40) + (created ? insets.bottom : 0) }} keyboardShouldPersistTaps="handled">
      <View style={{ flexDirection: 'row', gap: 6, marginBottom: compact ? 16 : 32 }}>
        {['Name', 'Responsibilities', 'Instructions', 'Avatar', 'Review'].map((label, index) => <Pressable key={label} disabled={busy || !!created || index >= progressStep} onPress={() => run(() => save(draft, index))} accessibilityRole="button" accessibilityLabel={`Back to ${label}`} style={{ flex: 1, gap: 9 }}>
          <View style={{ height: 3, borderRadius: 3, backgroundColor: index <= progressStep ? accent : colors.border_color_secondary }} />
          <Text size="xxs" color={index === progressStep ? 'primary' : 'secondary'}>{compact && label === 'Responsibilities' ? 'Purpose' : label}</Text>
        </Pressable>)}
      </View>
      <Text weight="bold" style={{ fontSize: compact ? 24 : 30, lineHeight: compact ? 30 : 38, marginBottom: compact ? 6 : 10 }}>{created ? `${created.name} is ready` : titles[Math.min(step, 3)]}</Text>
      <Text color="secondary" style={{ lineHeight: compact ? 20 : 24, marginBottom: compact ? 16 : 28 }}>{created ? 'Your bot is ready. Start a quick conversation or give it a job.' : compact && step === 2 ? 'Review Gab AI’s draft, edit it, or write your own.' : subtitles[Math.min(step, 3)]}</Text>
      {drafting && <View style={{ ...card, gap: 16 }} accessibilityLiveRegion="polite">
        <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}><ActivityIndicator color={accent} /><Text>Arya is drafting your bot’s setup…</Text></View>
        <Text size="s" color="secondary">Putting together responsibilities and tailored instructions.</Text>
        {[100, 86, 65].map(width => <View key={width} style={{ height: 10, width: `${width}%`, borderRadius: 5, backgroundColor: colors.border_color_secondary }} />)}
      </View>}
      {!created && !drafting && step === 0 && <View style={{ ...card, gap: 12 }}>
        <Text weight="bold">A name can say a lot.</Text>
        <Text color="secondary" style={{ lineHeight: 24 }}>Try a role like “Garden Planner” or a name like “Scout”. Arya will suggest a useful starting point, and you’ll have the final say.</Text>
        <View style={{ gap: 8 }}>
          {[...nameIdeas, ...(selectedName && !nameIdeas.includes(selectedName) ? [selectedName] : [])].map((name, index) => {
            const selected = selectedName === name;
            return <Pressable key={name} testID="bot-name-suggestion" disabled={busy} onPress={() => { setSelectedName(name); send(name); }} accessibilityRole="button" accessibilityLabel={name} accessibilityState={{ selected, disabled: busy }}
              style={({ hovered, pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 14, paddingVertical: 14, borderWidth: 1, borderRadius: 10,
                borderColor: selected ? accent : (hovered || pressed) ? colors.text_color_secondary : isDark ? 'rgba(255,255,255,0.16)' : colors.border_color_secondary,
                backgroundColor: selected ? brand.wash : (hovered || pressed) ? colors.solid_color_tertiary : colors.solid_color_primary,
                opacity: busy ? 0.5 : 1,
              })}>
              <View style={{ width: 22, height: 22, borderRadius: 5, borderWidth: 1, borderColor: selected ? accent : colors.text_color_secondary, alignItems: 'center', justifyContent: 'center' }}><Text size="xs" style={selected ? { color: accent } : undefined}>{selected ? '✓' : index + 1}</Text></View>
              <Text weight="medium" style={{ flex: 1 }}>{name}</Text>
            </Pressable>;
          })}
        </View>
        <Text size="s" color="secondary">Or write your own name in the composer below.</Text>
      </View>}
      {!created && !drafting && (step === 1 || step === 2) && <View style={{ gap: compact ? 12 : 16 }}>
        <View style={{ flexDirection: 'row', gap: 14, alignItems: 'center' }}>{avatar(compact ? 32 : 44)}<View style={{ flex: 1 }}><Text weight="bold">{draft.name}</Text><Text size="xs" color="secondary">{draft[FIELDS[step]] ? (step === 1 ? 'Responsibilities · Drafted by Gab AI' : 'System instructions · Drafted by Gab AI') : (step === 1 ? 'Responsibilities' : 'System instructions')}</Text></View></View>
        {!!draft[FIELDS[step]] && <View style={{ ...card, gap: compact ? 12 : 20 }}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Text size="xs" color="secondary">{step === 1 ? 'SUGGESTED RESPONSIBILITIES' : 'SUGGESTED SYSTEM INSTRUCTIONS'}</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
              {link(editedSuggestion === null ? 'Edit suggestion' : 'Cancel edits', () => setEditedSuggestion(editedSuggestion === null ? draft[FIELDS[step]] : null), undefined, 'xs')}
              {!keyboardVisible && link('Redraft with Gab AI', () => generate('setup'), undefined, 'xs')}
            </View>
          </View>
          {editedSuggestion === null ? <SetupTextPreview key={`${step}:${draft[FIELDS[step]]}`} text={draft[FIELDS[step]]} accent={accent} compact={compact} /> : <View style={{ gap: 8 }}>
            <TextInput multiline autoFocus value={editedSuggestion} onChangeText={setEditedSuggestion} editable={!busy} maxLength={LIMITS[step]} accessibilityLabel="Edit Arya suggestion"
              style={{ color: colors.text_color_primary, backgroundColor: colors.solid_color_primary, borderWidth: 1, borderColor: accent, borderRadius: 12, padding: 14, fontSize: 16, lineHeight: 26, height: compact ? 140 : step === 1 ? 160 : 280, textAlignVertical: 'top', ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}) }} />
            <Text size="xs" color="secondary">{editedSuggestion.length} / {LIMITS[step]} · Saved when you approve</Text>
          </View>}

        </View>}
        {!draft[FIELDS[step]] && <View style={{ ...card, gap: 14 }}>
          <Text weight="bold">{step === 1 ? 'What should this bot handle?' : 'How should this bot work?'}</Text>
          <Text color="secondary" style={{ lineHeight: 22 }}>Write the {step === 1 ? 'responsibilities' : 'instructions'} below and send them when you’re ready.</Text>
        </View>}
      </View>}
      {!created && step >= 3 && <View style={{ ...card, padding: compact ? 20 : 28, gap: 20 }}>
        <View style={{ alignItems: 'center', gap: 14 }}>
          <View style={{ padding: 8, borderRadius: 44, borderWidth: 1, borderColor: brand.frameBorder, backgroundColor: brand.frameBg }}>
            <View style={{ width: compact ? 128 : 160, height: compact ? 128 : 160, justifyContent: 'center', alignItems: 'center', overflow: 'hidden', borderRadius: 36 }}>
              {draft.avatarUrl ? avatar(compact ? 128 : 160) : generatingAvatar ? <ActivityIndicator size="large" color={accent} /> : avatar(compact ? 128 : 160)}
              {generatingAvatar && !!draft.avatarUrl && <View style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.45)', alignItems: 'center', justifyContent: 'center' }}><ActivityIndicator size="large" color="#fff" /></View>}
            </View>
          </View>
          <Text weight="bold" style={{ fontSize: 20, textAlign: 'center' }}>{draft.name}</Text>
          <Text size="s" color="secondary" accessibilityLiveRegion="polite" style={{ textAlign: 'center' }}>{generatingAvatar ? 'Arya is creating your avatar…' : draft.avatarUrl ? 'Your bot’s new look' : 'Let’s give your bot a face'}</Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 10 }}>
          {[{ label: draft.avatarUrl ? 'Regenerate avatar' : 'Generate avatar', icon: '↻', action: () => generate('avatar') }, { label: 'Upload avatar', icon: '↑', action: upload }].map(({ label, icon, action }) => <Pressable key={label} onPress={action} disabled={busy || generatingAvatar || (icon === '↻' && avatarLimitReached)} accessibilityRole="button" accessibilityLabel={label}
            style={({ hovered, pressed }) => ({ flex: 1, minHeight: 48, padding: 12, borderRadius: 12, borderWidth: 1, borderColor: colors.text_color_tertiary, backgroundColor: hovered || pressed ? colors.solid_color_tertiary : colors.solid_color_primary, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 8, opacity: busy || generatingAvatar || (icon === '↻' && avatarLimitReached) ? 0.45 : 1 })}>
            <Text style={{ color: accent, fontSize: 20 }}>{icon}</Text><Text size="s" weight="medium" style={{ flexShrink: 1 }}>{label}</Text>
          </Pressable>)}
        </View>
        <Text size="s" color="secondary" accessibilityLiveRegion="polite" style={{ textAlign: 'center', lineHeight: 22 }}>{avatarLimitReached ? 'You’ve explored all 5 avatar generations. Keep this look, or upload your own to make it just right.' : `${avatarVariationsLeft} avatar generations left · You can also upload your own.`}</Text>
        <Text size="xs" color="secondary" style={{ textAlign: 'center' }}>PNG or JPG · Up to 5 MB</Text>
      </View>}
      {created && <View style={{ gap: 20 }}>
        <View style={{ alignItems: 'center' }}>{avatar(compact ? 72 : 88)}</View>
        <BotModeChoices disabled={busy}
          onQuickChat={() => run(async () => { await startChat(created); })}
          onWork={() => run(async () => { await startWork(created, { source: 'bot_onboarding', requestTask: true }); })} />
        <View style={{ alignItems: 'center' }}>{link('Back to my bots', () => router.replace('/agents'))}</View>
      </View>}
      {!created && step >= 3 && !!avatarError && <Text color="danger" accessibilityRole="alert">{avatarError}</Text>}
      {!!error && <View accessibilityLiveRegion="polite" style={{ marginTop: 20, gap: 8 }}><Text color="danger" accessibilityRole="alert">{error}</Text></View>}
      {busy && !drafting && !generatingAvatar && <ActivityIndicator color={accent} style={{ marginTop: 16 }} />}
    </ScrollView>
    {!created && step >= 3 && <View style={{ flexShrink: 0, paddingHorizontal: compact ? 16 : 24, paddingTop: 12, paddingBottom: 16 + insets.bottom, backgroundColor: colors.solid_color_primary, borderTopWidth: 1, borderColor: colors.border_color_secondary }}>
      <View style={{ width: '100%', maxWidth: 712, alignSelf: 'center' }}>
        <Pressable onPress={create} disabled={busy || generatingAvatar || !draft.avatarUrl} accessibilityRole="button" accessibilityLabel="Create bot" accessibilityState={{ disabled: busy || generatingAvatar || !draft.avatarUrl, busy }}
          style={({ hovered, pressed }) => ({ minHeight: 50, borderRadius: 14, paddingHorizontal: 24, paddingVertical: 14, flexDirection: 'row', gap: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.text_color_primary, opacity: busy || generatingAvatar || !draft.avatarUrl ? 0.35 : hovered || pressed ? 0.85 : 1 })}>
          {busy && <ActivityIndicator color={colors.solid_color_primary} size="small" />}
          <Text weight="bold" style={{ color: colors.solid_color_primary }}>{busy ? 'Creating your bot…' : 'Create bot'}</Text>
          {!busy && <Text style={{ color: colors.solid_color_primary }}>→</Text>}
        </Pressable>
      </View>
    </View>}
    {!created && step < 3 && <View testID="bot-setup-footer" style={{ flexShrink: 0, backgroundColor: colors.solid_color_primary, paddingHorizontal: compact ? 16 : 24, paddingTop: compact ? 4 : 12, paddingBottom: (compact ? 8 : 16) + (keyboardVisible ? 0 : insets.bottom), width: '100%', maxWidth: 760, alignSelf: 'center', gap: 10 }}>
      {!drafting && (step === 1 || step === 2) && !!draft[FIELDS[step]] && (
        <View testID="bot-setup-approve-actions" style={{ width: '100%', alignItems: 'center', justifyContent: 'center' }}>
          {approveButton}
        </View>
      )}
      {(!native || editedSuggestion === null) && <BotSetupComposer maxInputHeight={native ? (keyboardVisible ? 96 : Math.min(160, height * 0.2)) : 240} value={input} onChangeText={setInput} maxLength={LIMITS[step]}
        busy={busy} label={LABELS[step]} placeholder={step === 0 ? 'Name your bot…' : drafting ? 'Arya is putting your draft together…' : step === 1 ? 'Write your own responsibilities…' : 'Write your own system instructions…'} onSend={() => send()} />}
      {!keyboardVisible && <Text size="xxs" color="secondary" style={{ textAlign: 'center' }}>{step === 0 ? 'A little setup. A lot of possibilities.' : draft[FIELDS[step]] ? 'Approve the suggestion above, or send your own' : 'Write your own and send to continue'} · No credits used</Text>}
    </View>}
  </KeyboardAvoidingView>;
}
